import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    Alert, Autocomplete, Box, Button, Chip, FormControl, FormControlLabel, FormLabel, Paper, Radio, RadioGroup,
    Tab, Tabs, TextField, Typography,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { RoleService } from '../../services/role.service';
import { ICatalog, IRole, Scope } from '../../interfaces/role.interface';
import PermissionTree from './PermissionTree';
import RoleMembersTab from './RoleMembersTab';
import { helpWhy } from '../../help/why';
import { MODULE_CATALOG } from '../modules/moduleCatalog';

const MAX_MAIN_MODULES = 5;
/** Catalog cards that appear in the left menu (the Dashboard card is always first, so it is not offered). */
const MAIN_MODULE_OPTIONS = MODULE_CATALOG
    // Institutions and SaaS Operator are platform menus no institution role ever sees.
    .filter(c => c.nav && !['dashboards', 'institutions', 'saasOperator'].includes(c.key))
    .map(c => ({ key: c.key, label: c.nav?.label ?? c.label }))
    .sort((a, b) => a.label.localeCompare(b.label));

const RoleEditorPage: React.FC = () => {
    const { roleId } = useParams<{ roleId: string }>();
    const [searchParams] = useSearchParams();
    const templateKey = searchParams.get('template');
    const isEditing = !!roleId;

    const { selectedInstitutionId, refreshPermissions } = useAuth();
    const toast = useToast();
    const navigate = useNavigate();

    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [catalog, setCatalog] = useState<ICatalog | null>(null);
    const [role, setRole] = useState<IRole | null>(null);

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [scope, setScope] = useState<Scope>('INSTITUTION');
    const [keys, setKeys] = useState<Set<string>>(new Set());
    const [mainModules, setMainModules] = useState<string[]>([]);

    useEffect(() => {
        if (!selectedInstitutionId) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const catalogPromise = RoleService.catalog(selectedInstitutionId);
                if (isEditing && roleId) {
                    const [cat, existingRole] = await Promise.all([catalogPromise, RoleService.get(selectedInstitutionId, roleId)]);
                    if (cancelled) return;
                    setCatalog(cat);
                    setRole(existingRole);
                    setName(existingRole.name);
                    setDescription(existingRole.description ?? '');
                    setScope(existingRole.scope);
                    setKeys(new Set(existingRole.keys));
                    setMainModules(existingRole.mainModules ?? []);
                } else {
                    const [cat, templates] = await Promise.all([catalogPromise, RoleService.templates(selectedInstitutionId)]);
                    if (cancelled) return;
                    setCatalog(cat);
                    const template = templateKey ? templates.find(t => t.key === templateKey) : undefined;
                    if (template) {
                        setName(template.displayName);
                        setDescription(template.description);
                        setScope(template.scope);
                        setKeys(new Set(template.keys));
                        setMainModules(template.mainModules ?? []);
                    }
                }
            } catch (err) {
                if (!cancelled) toast.showApiError(err, 'Failed to load role details');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedInstitutionId, roleId, templateKey, isEditing]);

    const handleSave = async () => {
        if (!selectedInstitutionId || !name.trim()) return;
        setSaving(true);
        try {
            const dto = { name: name.trim(), description: description.trim() || null, scope, keys: Array.from(keys), mainModules };
            if (isEditing && roleId) {
                await RoleService.update(selectedInstitutionId, roleId, dto);
            } else {
                await RoleService.create(selectedInstitutionId, templateKey ? { ...dto, templateKey } : dto);
            }
            await refreshPermissions();
            toast.showSuccess('Role saved');
            navigate('/roles');
        } catch (err) {
            toast.showApiError(err, 'Failed to save role');
        } finally {
            setSaving(false);
        }
    };

    if (loading || !catalog) {
        return <Typography sx={{ p: 3 }}>Loading…</Typography>;
    }

    const readOnly = !!role?.isAdmin;

    return (
        <Box sx={{ p: 3 }} data-help-page="roles.editor.page">
            <Typography variant="h5" sx={{ mb: 2 }}>{isEditing ? 'Edit role' : 'New role'}</Typography>
            {readOnly && (
                <Alert severity="info" sx={{ mb: 2 }}>Locked — Institution Admin always has everything</Alert>
            )}
            <Paper variant="outlined">
                <Tabs value={tab} onChange={(_e, v) => setTab(v)}>
                    <Tab label="Details" data-help="roles.editor.tab.details" />
                    <Tab label="Features & actions" data-help="roles.editor.tab.permissions" />
                    {isEditing && <Tab label="Members" data-help="roles.editor.tab.members" />}
                </Tabs>
                <Box sx={{ p: 2 }}>
                    {tab === 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 480 }} data-help-section="roles.editor.section.details">
                            <TextField
                                label="Name"
                                data-help="roles.editor.field.name"
                                value={name}
                                onChange={e => setName(e.target.value)}
                                disabled={readOnly}
                                required
                                fullWidth
                                inputProps={{ maxLength: 100 }}
                            />
                            <TextField
                                label="Description"
                                data-help="roles.editor.field.description"
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                                disabled={readOnly}
                                multiline
                                minRows={2}
                                fullWidth
                                inputProps={{ maxLength: 500 }}
                            />
                            <Autocomplete
                                multiple
                                data-help="roles.editor.field.mainModules"
                                options={MAIN_MODULE_OPTIONS}
                                value={mainModules.map(k => MAIN_MODULE_OPTIONS.find(o => o.key === k) ?? { key: k, label: k })}
                                onChange={(_e, v) => setMainModules(v.slice(0, MAX_MAIN_MODULES).map(o => o.key))}
                                getOptionLabel={o => o.label}
                                isOptionEqualToValue={(a, b) => a.key === b.key}
                                getOptionDisabled={o => mainModules.length >= MAX_MAIN_MODULES && !mainModules.includes(o.key)}
                                disabled={readOnly}
                                renderTags={(value, getTagProps) => value.map((o, index) => {
                                    const { key: tagKey, ...tagProps } = getTagProps({ index });
                                    return <Chip key={tagKey} size="small" label={o.label} {...tagProps} />;
                                })}
                                renderInput={params => (
                                    <TextField {...params} label="Main modules"
                                        helperText={`Shown right after Dashboard in the left menu, in this order (max ${MAX_MAIN_MODULES}).`} />
                                )}
                            />
                            <FormControl disabled={readOnly} data-help="roles.editor.field.scope">
                                <FormLabel id="role-scope-label">Scope</FormLabel>
                                <RadioGroup
                                    aria-labelledby="role-scope-label"
                                    value={scope}
                                    onChange={e => setScope(e.target.value as Scope)}
                                >
                                    <FormControlLabel value="OWN" data-help="roles.editor.scope.own" control={<Radio />} label="Own" />
                                    <FormControlLabel value="DEPARTMENT" data-help="roles.editor.scope.department" control={<Radio />} label="Department" />
                                    <FormControlLabel value="INSTITUTION" data-help="roles.editor.scope.institution" control={<Radio />} label="Institution" />
                                </RadioGroup>
                            </FormControl>
                        </Box>
                    )}
                    {tab === 1 && (
                        <PermissionTree catalog={catalog} selected={keys} onChange={setKeys} readOnly={readOnly} />
                    )}
                    {tab === 2 && isEditing && role && selectedInstitutionId && (
                        <RoleMembersTab institutionId={selectedInstitutionId} role={role} />
                    )}
                </Box>
            </Paper>
            {!readOnly && (
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <Button variant="contained" disabled={!name.trim() || saving} onClick={handleSave} data-help="roles.editor.save" {...helpWhy(!saving && !name.trim() ? { kind: 'status', message: 'Type a name for the role first.' } : null)}>Save</Button>
                    <Button variant="text" onClick={() => navigate('/roles')} data-help="roles.editor.cancel">Cancel</Button>
                </Box>
            )}
        </Box>
    );
};

export default RoleEditorPage;
